import { useCallback, useMemo, useRef, useState } from 'react'
import { calendarFiltersForKinds } from './categories'
import { isLiveRangeSet } from './liveRange'
import { countCalendarFilters } from './matcher'
import {
  type CalendarItemKind,
  type CalendarCategoryFilter,
  type CalendarFilterCategory,
  type CalendarDateRangeFilter,
  type CalendarFilterMode,
  type CalendarFilterState,
  EMPTY_CALENDAR_FILTERS,
  EMPTY_CATEGORY_FILTER,
} from './types'
import {
  categoryFilterOf,
  type CalendarValueTree,
  expandValues,
  impliedFilterMode,
  normalizeCategoryFilter,
  otherFilterMode,
  parentFilterMode,
} from './valueTree'

export type UseCalendarFiltersResult = Readonly<{
  /** What the calendar filters by. Every edit below changes it at once. */
  state: CalendarFilterState
  /**
   * The number on the filter button: every chosen value that anything on the
   * calendar could carry. A value only a hidden kind could carry is kept in
   * `state` and left out of this, because the drawer shows it nowhere.
   */
  count: number
  clearAll: () => void
  /**
   * Replaces everything at once. It is how a month with no filters of its own
   * takes over the week view's, and it is announced like any other edit, so
   * what is taken over is written to the address and remembered from then on.
   */
  replaceAll: (state: CalendarFilterState) => void
  /**
   * Drops one value from the list that holds it.
   *
   * Pass the category's value tree wherever it offers a parent to choose: the
   * carve-outs under a dropped row go with it.
   */
  removeValue: (
    categoryId: string,
    mode: CalendarFilterMode,
    valueId: string,
    tree?: CalendarValueTree
  ) => void
  /**
   * Sets the days an item has to be live on. A window with neither end set is
   * no window, and is dropped rather than remembered as an empty one.
   */
  setLiveRange: (range: CalendarDateRangeFilter) => void
  setMode: (categoryId: string, mode: CalendarFilterMode) => void
  /**
   * Sets the mode of one row of a category whose rows each answer for
   * themselves: one label set matched while the next is hidden.
   *
   * It moves that row's ticks and the carve-outs under them between the two
   * lists, and leaves every other row where it is. A row with nothing ticked in
   * it has nothing to move, so it goes on reading Include until it holds
   * something.
   */
  setParentMode: (
    categoryId: string,
    parentId: string,
    mode: CalendarFilterMode,
    tree: CalendarValueTree
  ) => void
  /**
   * Chooses every value named, or clears them all where all are chosen. Values
   * not named stay as they are, so pass the rows a search leaves on show.
   *
   * Pass the tree wherever the category offers a parent to choose, so Select
   * all writes the parents and not every value under them as well.
   */
  toggleAllValues: (
    categoryId: string,
    optionIds: readonly string[],
    tree?: CalendarValueTree
  ) => void
  /**
   * Chooses one value, or drops it again.
   *
   * Pass the category's value tree wherever it offers a parent to choose. A
   * parent then stands for its children — choosing it drops theirs — and
   * unchoosing a child of a chosen parent carves that child out rather than
   * saying nothing at all.
   */
  toggleValue: (
    categoryId: string,
    valueId: string,
    tree?: CalendarValueTree
  ) => void
}>

const listFor = (category: CalendarCategoryFilter): CalendarFilterMode =>
  category.mode ?? 'include'

/**
 * Which list one value is chosen into: the category's, or the one belonging to
 * the row it sits under where each row answers for itself.
 */
const listForValue = (
  category: CalendarCategoryFilter,
  valueId: string,
  tree: CalendarValueTree | undefined
): CalendarFilterMode =>
  tree?.modeScope === 'parent'
    ? parentFilterMode(valueId, category, tree)
    : listFor(category)

const withCategory = (
  state: CalendarFilterState,
  categoryId: string,
  change: (category: CalendarCategoryFilter) => CalendarCategoryFilter
): CalendarFilterState => ({
  ...state,
  cats: {
    ...state.cats,
    [categoryId]: change(state.cats[categoryId] ?? EMPTY_CATEGORY_FILTER),
  },
})

const toggle = (values: readonly string[], valueId: string): string[] =>
  values.includes(valueId)
    ? values.filter((value) => value !== valueId)
    : [...values, valueId]

const without = (
  values: readonly string[],
  unwanted: readonly string[]
): string[] => values.filter((value) => !unwanted.includes(value))

/**
 * A state written by hand, such as a story's, is read the way the address is:
 * a category that names no mode takes the one its lists say. Include is what a
 * category does anyway, so only a category that hides has it written in.
 */
const withImpliedModes = (state: CalendarFilterState): CalendarFilterState => ({
  ...state,
  cats: Object.fromEntries(
    Object.entries(state.cats).map(
      ([id, category]): [string, CalendarCategoryFilter] => [
        id,
        category.mode === undefined && impliedFilterMode(category) === 'exclude'
          ? { ...category, mode: 'exclude' }
          : category,
      ]
    )
  ),
})

/**
 * What choosing one value does to a category that is a tree.
 *
 * A parent is chosen on its own behalf and on its children's. A child of a
 * chosen parent is already in the filter, so choosing it can only mean taking
 * it out again: the carve-out, written into the other list. That says "all of
 * this channel except that account" while the category matches, and "none of
 * this channel except that account" while it hides.
 */
const toggleInTree = (
  category: CalendarCategoryFilter,
  mode: CalendarFilterMode,
  valueId: string,
  tree: CalendarValueTree
): CalendarCategoryFilter => {
  const chosen = category[mode]
  const carved = category[otherFilterMode(mode)]
  const children = tree.childrenOf.get(valueId)

  if (children !== undefined)
    return categoryFilterOf(
      mode,
      chosen.includes(valueId)
        ? without(chosen, [valueId, ...children])
        : [...without(chosen, children), valueId],
      // The carve-outs go with the row they were carved from: they were
      // exceptions to a rule that is gone.
      without(carved, [valueId, ...children])
    )

  const parentId = tree.parentOf.get(valueId)
  // Chosen through the row above it, whether or not it is written down as well:
  // Select all writes every child, and unchecking one there means what it means
  // anywhere else.
  if (parentId === undefined || !chosen.includes(parentId))
    return categoryFilterOf(mode, toggle(chosen, valueId), carved)

  return carved.includes(valueId)
    ? categoryFilterOf(mode, chosen, without(carved, [valueId]))
    : categoryFilterOf(mode, without(chosen, [valueId]), [...carved, valueId])
}

/**
 * The filter state the calendar reads, and the edits the drawer makes to it.
 *
 * Filtering runs in the browser over items that have already arrived, so there
 * is nothing to wait for and nothing to batch: a value is chosen and the
 * calendar answers. `onChange` is told each new state, which is where a surface
 * writes it to the address or remembers it.
 */
export const useCalendarFilters = ({
  categories = [],
  initialState = EMPTY_CALENDAR_FILTERS,
  onChange,
  shownKinds = [],
}: {
  /**
   * Every category the surface knows, with every option each of them offers —
   * not the narrowed list the drawer is given. `count` reads these to tell a
   * value that is filtering from one that is only being kept, and a value whose
   * option cannot be found is counted, because an option list that has not
   * arrived is not an answer. Leave them out and every chosen value counts.
   */
  categories?: readonly CalendarFilterCategory[]
  initialState?: CalendarFilterState
  onChange?: (state: CalendarFilterState) => void
  /** The kinds of item on the calendar now. */
  shownKinds?: readonly CalendarItemKind[]
} = {}): UseCalendarFiltersResult => {
  const [state, setState] = useState(() => withImpliedModes(initialState))

  // The latest state and the latest listener, so the edits below can be built
  // once and never go stale.
  const current = useRef(state)
  const listener = useRef(onChange)
  listener.current = onChange

  const edit = useCallback(
    (change: (state: CalendarFilterState) => CalendarFilterState) => {
      const next = change(current.current)
      current.current = next
      setState(next)
      listener.current?.(next)
    },
    []
  )

  // The mode belongs to the category, so switching it turns everything the
  // category holds around: the channels a person was matching become the
  // channels they are hiding, and the accounts carved out of them are the
  // accounts kept back from what is hidden.
  const setMode = useCallback(
    (categoryId: string, mode: CalendarFilterMode) =>
      edit((state) =>
        withCategory(state, categoryId, (category) =>
          listFor(category) === mode
            ? category
            : categoryFilterOf(
                mode,
                category[otherFilterMode(mode)],
                category[mode]
              )
        )
      ),
    [edit]
  )

  const setLiveRange = useCallback(
    (range: CalendarDateRangeFilter) =>
      edit(({ cats }) =>
        isLiveRangeSet(range) ? { cats, live: range } : { cats }
      ),
    [edit]
  )

  const setParentMode = useCallback(
    (
      categoryId: string,
      parentId: string,
      mode: CalendarFilterMode,
      tree: CalendarValueTree
    ) =>
      edit((state) =>
        withCategory(state, categoryId, (category) => {
          if (parentFilterMode(parentId, category, tree) === mode)
            return category

          const under = [parentId, ...(tree.childrenOf.get(parentId) ?? [])]
          const mine = (values: readonly string[]) =>
            values.filter((value) => under.includes(value))
          const rest = (values: readonly string[]) =>
            values.filter((value) => !under.includes(value))

          // The row's ticks take the list it is moving to, and the carve-outs
          // under them take the list it is leaving: "all of this set except
          // that label" reads the same either way round.
          const from = otherFilterMode(mode)
          const moved = (list: CalendarFilterMode) =>
            list === mode
              ? [...rest(category[mode]), ...mine(category[from])]
              : [...rest(category[from]), ...mine(category[mode])]

          // A row with nothing to move has nothing to say it with, so the
          // answer is remembered until its first tick lands in the right list.
          return normalizeCategoryFilter(
            {
              exclude: moved('exclude'),
              include: moved('include'),
              parentModes: { ...category.parentModes, [parentId]: mode },
            },
            tree
          )
        })
      ),
    [edit]
  )

  const toggleValue = useCallback(
    (categoryId: string, valueId: string, tree?: CalendarValueTree) =>
      edit((state) =>
        withCategory(state, categoryId, (category) => {
          const mode = listForValue(category, valueId, tree)

          return tree === undefined
            ? categoryFilterOf(
                mode,
                toggle(category[mode], valueId),
                category[otherFilterMode(mode)]
              )
            : normalizeCategoryFilter(
                {
                  ...toggleInTree(category, mode, valueId, tree),
                  parentModes: category.parentModes,
                },
                tree
              )
        })
      ),
    [edit]
  )

  // Select all is all or none of the values it names, and leaves every other
  // value where it is: a search narrows it to the rows on show. Where the
  // category is a tree, a value under a chosen row is already chosen, and what
  // is written down is the rows: "All Facebook", never Facebook and each of
  // its accounts beside it. So clearing a value chosen through its row carves
  // it out, and choosing a row takes the carve-outs under it with it.
  const toggleAllValues = useCallback(
    (
      categoryId: string,
      optionIds: readonly string[],
      tree?: CalendarValueTree
    ) =>
      edit((state) =>
        withCategory(state, categoryId, (category) => {
          // Where each row answers for itself, the values named all sit under
          // the one row whose tier Select all is drawn on.
          const mode =
            optionIds.length === 0
              ? listFor(category)
              : listForValue(category, optionIds[0], tree)
          const chosen = category[mode]
          const carved = category[otherFilterMode(mode)]
          const underChosenRow = (optionId: string) => {
            const parentId = tree?.parentOf.get(optionId)
            return parentId !== undefined && chosen.includes(parentId)
          }
          const isChosen = (optionId: string) =>
            chosen.includes(optionId) ||
            (underChosenRow(optionId) && !carved.includes(optionId))
          const allChosen = optionIds.length > 0 && optionIds.every(isChosen)
          const named = [...expandValues(optionIds, tree)]
          const kept = without(chosen, allChosen ? named : optionIds)
          const next = allChosen
            ? categoryFilterOf(mode, kept, [
                ...without(carved, named),
                ...optionIds.filter((optionId) => {
                  const parentId = tree?.parentOf.get(optionId)
                  return parentId !== undefined && kept.includes(parentId)
                }),
              ])
            : categoryFilterOf(
                mode,
                [
                  ...kept,
                  ...optionIds.filter((optionId) => !underChosenRow(optionId)),
                ],
                without(carved, named)
              )

          return tree === undefined
            ? next
            : normalizeCategoryFilter(
                { ...next, parentModes: category.parentModes },
                tree
              )
        })
      ),
    [edit]
  )

  // Pass the tree wherever the category offers a parent to choose: dropping a
  // row takes the carve-outs under it with it, and a carve-out left behind
  // would filter from nowhere a person could see.
  const removeValue = useCallback(
    (
      categoryId: string,
      mode: CalendarFilterMode,
      valueId: string,
      tree?: CalendarValueTree
    ) =>
      edit((state) =>
        withCategory(state, categoryId, (category) => {
          const own = listFor(category)
          const kept = category[mode].filter((value) => value !== valueId)
          const next = {
            ...(mode === own
              ? categoryFilterOf(own, kept, category[otherFilterMode(own)])
              : categoryFilterOf(own, category[own], kept)),
            parentModes: category.parentModes,
          }

          return tree === undefined ? next : normalizeCategoryFilter(next, tree)
        })
      ),
    [edit]
  )

  const clearAll = useCallback(() => edit(() => EMPTY_CALENDAR_FILTERS), [edit])

  const replaceAll = useCallback(
    (next: CalendarFilterState) => edit(() => next),
    [edit]
  )

  // What is filtering, not what is written down. A moment status chosen before
  // the moment row was turned off is kept in the state and drawn nowhere, so
  // counting it would put a number on the filter button that no row, no token
  // and no badge in the drawer could account for.
  const count = useMemo(
    () =>
      countCalendarFilters(
        calendarFiltersForKinds(state, categories, shownKinds)
      ),
    [categories, shownKinds, state]
  )

  return {
    clearAll,
    count,
    removeValue,
    replaceAll,
    setLiveRange,
    setMode,
    setParentMode,
    state,
    toggleAllValues,
    toggleValue,
  }
}
